import {
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ChatbotProviderProps, ChatEvent } from "@altimateai/ui-components/chatbotV2";
import { useQuery } from "@tanstack/react-query";
import { LoadingButton, Stack } from "@altimateai/lego";
import { processPlaygroundSessionTurns } from "@altimateai/ui-components/chatbotV2";
import { RelativeComponentLoader } from "@/components/loader";
import { Button, SidebarInset, Typography } from "@altimateai/lego";
import { SidebarProvider } from "@altimateai/lego";
import PlaygroundHelloMessage from "./PlaygroundHelloMessage";
import PlaygroundSidebar from "./PlaygroundSidebar";
import { getPlaygroundSessionDetails } from "@/helpers/apis";
import { useConnections } from "@/hooks/useConnections";
import { IconRefresh, IconPlus } from "@tabler/icons-react";
import PlaygroundScheduleAction from "./PlaygroundScheduleAction";
import StudioShareButton from "./StudioShareButton";
import ConditionalSidebarTrigger from "./ConditionalSidebarTrigger";
import PlaygroundSessionTitle from "./PlaygroundSessionTitle";
import { useSidebarExpansion } from "@/hooks";
import { PlaygroundHelloMessageFooter } from "@/pages/playground/PlaygroundHelloMessageFooter";
import PlaygroundChatbot, {
  PlaygroundChatbotRef,
} from "@/modules/playground/components/PlaygroundChatbot";
import { cn } from "@/lib/utils";
import { useBreadcrumbContext } from "@/components/breadcrumbs/BreadcrumbsProvider";
import { StudioTracking } from "@/lib/tracking/studio";
import { useAppState } from "@/modules/app/useAppContext";

const Playground = () => {
  const { clear: clearBreadcrumbs } = useBreadcrumbContext();
  useLayoutEffect(() => {
    clearBreadcrumbs();
  }, [clearBreadcrumbs]);
  const ref = useRef<PlaygroundChatbotRef>(null);
  const promptProcessedRef = useRef(false);
  const { session_id } = useParams<{ session_id?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Use connections hook
  const { selectedConnections, isLoading: isLoadingConnections } = useConnections();
  // State for initial message from prompt library or query param
  const [initialMessage, setInitialMessage] = useState<string>(location.state?.message || "");
  const isNewSession = location.state?.isNewSession;

  const centerCssClass = "al-max-w-6xl !al-w-[50vw] al-m-auto";

  // Check for prompt query param and use it as initial message
  useEffect(() => {
    if (promptProcessedRef.current) return;

    const promptFromUrl = searchParams.get("prompt");
    if (promptFromUrl) {
      promptProcessedRef.current = true;
      setInitialMessage(promptFromUrl);
      // Remove the prompt param from URL
      setSearchParams(
        prev => {
          prev.delete("prompt");
          return prev;
        },
        { replace: true }
      );
    }
  }, [searchParams, setSearchParams]);

  // Track session resumption when navigating to an existing session
  useEffect(() => {
    if (session_id) {
      StudioTracking.sessionResumed(session_id);
    }
  }, [session_id]);

  // Collapse sidebar on mount, expand on unmount
  useSidebarExpansion({
    expandOnMount: false,
    collapseOnUnmount: false,
    hideExpandToggleIconOnMount: true,
  });

  // Fetch session details using React Query
  const {
    data: sessionDetails,
    isLoading: loadingSession,
    error: sessionError,
    refetch,
  } = useQuery({
    queryKey: ["playground-session", session_id],
    queryFn: () => getPlaygroundSessionDetails(session_id!),
    enabled: !!session_id,
  });

  const getSessionContext = useCallback(() => {
    if (!sessionDetails?.session.context) return { connections: selectedConnections };
    return { connections: selectedConnections, ...sessionDetails.session.context };
  }, [sessionDetails, selectedConnections]);

  // Memoize the context object to avoid creating new reference on every render
  const contextValue = useMemo(() => {
    return getSessionContext();
  }, [getSessionContext]);

  const sessions = useMemo(() => {
    if (!sessionDetails) {
      if (session_id) {
        const session = sessionStorage.getItem(session_id);
        if (session) {
          sessionStorage.removeItem(session_id);
          return { [session_id]: JSON.parse(session) };
        }
      }
      return {};
    }

    return processPlaygroundSessionTurns(
      sessionDetails.turns.map(turn => ({
        role: turn.role,
        content: turn.content,
        id: turn.id,
        timestamp: turn.timestamp,
        attachments: turn.attachments || [],
      })) as Parameters<typeof processPlaygroundSessionTurns>[0],
      sessionDetails.session.session_id,
      sessionDetails.session.datamates || [],
      getSessionContext(),
      sessionDetails.session.status
    );
  }, [session_id, sessionDetails, getSessionContext]);

  // Handle chat events to update URL when session is created
  const onChatEvent = useCallback(
    (event: ChatEvent) => {
      if (event.type === "status_change" && event.status === "pending") {
        const session = ref.current?.getSessionById(event.sessionId);
        if (session) {
          const lastUserMessage = [...session.messages].reverse().find(m => m.role === "user");
          StudioTracking.messageSent(
            event.sessionId,
            session.datamates?.[0] ?? null,
            lastUserMessage?.content?.length ?? 0
          );
        }
      }
      if (event.type === "status_change" && event.status === "complete") {
        // add session id to url for new session on completing first request
        if (!session_id) {
          const session = ref.current?.getSessionById(event.sessionId);
          if (session) {
            sessionStorage.setItem(event.sessionId, JSON.stringify(session));
          }
          navigate(`/studio/${event.sessionId}`, {
            replace: true,
            state: { isNewSession: true },
          });
        }
      }
    },
    [session_id, navigate]
  );

  const onNewChat = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Respect browser new-tab behavior when modifier keys are used (cmd/ctrl-click)
    if (e.ctrlKey || e.metaKey) {
      return;
    }

    // Reset the current tab session so a plain click always starts fresh.
    ref.current?.resetSession();
    setInitialMessage("");
  };

  const handleOpenPromptLibrary = () => {
    ref.current?.openPromptLibrary();
  };

  const showLoader = isLoadingConnections;
  const showSessionLoading = !isNewSession && loadingSession;

  const sessionName = sessionDetails?.session.name ?? "";
  const { enableStudioShareableLinks } = useAppState();

  // Memoize chatProviderProps to prevent creating new object reference on every render
  const chatProviderProps = useMemo(
    () =>
      ({
        context: contextValue,
        currentSessionId: session_id,
        chatboxStartPosition: "middle" as const,
        initialMessage: initialMessage,
        helloMessage: !loadingSession && !sessionError && <PlaygroundHelloMessage />,
        helloMessageFooter: !loadingSession && !sessionError && (
          <PlaygroundHelloMessageFooter onOpenPromptLibrary={handleOpenPromptLibrary} />
        ),
        sessions,
        onEvent: onChatEvent,
        // Drives the chatbot's built-in RefetchSession poller for PENDING turns (AI-5866).
        refetchSession: refetch,
        components: {
          messageActions: ({ sessionId: msgSessionId }) => (
            <PlaygroundScheduleAction sessionId={msgSessionId} sessionName={sessionName} />
          ),
          // Replace the default ShareMessage (EmailDialog) with the Studio
          // Share button (Link/Slack/Email tabs) only when the tenant has
          // shareable links enabled. When the flag is off, leave the slot
          // unset so ChatMessageActions falls back to the default
          // ShareMessage → EmailDialog flow. AI-6343.
          ...(enableStudioShareableLinks && {
            shareButton: ({ sessionId: msgSessionId }: { sessionId?: string }) => (
              <StudioShareButton sessionId={msgSessionId} sessionName={sessionName} />
            ),
          }),
        },
        classNames: {
          chatbot: "al-max-w-none al-w-full al-bg-transparent al-border-none !al-shadow-none",
          chatbotScrollingContent: centerCssClass,
          questionForm: `${centerCssClass} al-px-0`,
        },
      }) satisfies Partial<ChatbotProviderProps>,
    [
      contextValue,
      session_id,
      initialMessage,
      loadingSession,
      sessionError,
      sessions,
      onChatEvent,
      refetch,
      sessionName,
      enableStudioShareableLinks,
    ]
  );

  const chatbotKey = useMemo(() => {
    return `${session_id || "new-chat"}-${initialMessage ? `prompt-${crypto.randomUUID()}` : ""}`;
  }, [initialMessage, session_id]);

  return (
    <SidebarProvider className="studio-page al-absolute al-top-0 al-h-[calc(100vh-8px)] al-left-0 !al-w-[calc(100vw-48px)]">
      <PlaygroundSidebar onNewChat={onNewChat} />
      <SidebarInset className="!al-bg-[#F9FAFA] al-relative al-h-[calc(100%-80px)] al-pt-12 al-min-h-[70vh] al-min-h-auto">
        <Stack className="al-h-full al-relative al-w-full al-pt-8" direction="column">
          <ConditionalSidebarTrigger />
          {showLoader ? (
            <RelativeComponentLoader label="Loading..." />
          ) : (
            <>
              {(loadingSession || sessionError || sessionDetails) && (
                <div className={cn(loadingSession ? "al-flex-1" : "", centerCssClass)}>
                  {showSessionLoading ? (
                    <Stack direction="row" align="center" gap="sm" justify="center">
                      <RelativeComponentLoader label="Loading session..." componentHeight={20} />
                    </Stack>
                  ) : sessionError ? (
                    <Stack
                      direction="column"
                      justify="center"
                      align="center"
                      className="al-text-red-500 al-h-[90vh]"
                    >
                      <Typography variant="h3">Error Loading Session</Typography>
                      <Typography variant="body">
                        {sessionError instanceof Error
                          ? sessionError.message
                          : "Failed to load session"}
                      </Typography>
                      <Stack className="al-mt-4">
                        <LoadingButton
                          loading={loadingSession}
                          variant="secondary"
                          onClick={() => refetch()}
                        >
                          <IconRefresh />
                          Retry
                        </LoadingButton>
                        <Button asChild>
                          <Link
                            to="/studio"
                            onClick={onNewChat}
                            aria-label="open new chat on error"
                          >
                            <IconPlus className="al-align-text-top al-mr-2 al-inline-block" />
                            New Chat
                          </Link>
                        </Button>
                      </Stack>
                    </Stack>
                  ) : sessionDetails ? (
                    <PlaygroundSessionTitle
                      title={
                        sessionDetails.session.session_intent?.trim() || sessionDetails.session.name
                      }
                    />
                  ) : null}
                </div>
              )}

              {!sessionError && (
                <>
                  <Stack
                    justify="center"
                    className={cn("al-pb-0 al-min-h-0 ", loadingSession ? "" : "al-flex-1")}
                  >
                    <Suspense fallback={<RelativeComponentLoader label={"Loading Chatbot"} />}>
                      <PlaygroundChatbot
                        key={chatbotKey}
                        ref={ref}
                        enableImprovePrompt={true}
                        chatProviderProps={chatProviderProps}
                      />
                    </Suspense>
                  </Stack>
                  <Typography size="xs" className={cn(centerCssClass, "al-text-center al-pb-2")}>
                    AI can make mistakes. Review for accuracy.
                  </Typography>
                </>
              )}
            </>
          )}
        </Stack>
      </SidebarInset>
    </SidebarProvider>
  );
};

export default Playground;
